<template>
  <div
    class="box"
    :data-category-id="$props.categoryId"
    :data-group-id="$props.groupId"
    :data-product-id="productId"
  >
    <div
      class="body-2"
      v-html="$t(`products.${productId}.name`)"
    />

    <div
      class="product__designation h3"
      v-html="$t(`products.${productId}.designation`)"
    />

    <div class="product__pricing">
      <basic-product-pricing :product="$props.product" />
    </div>

    <div>
      <div
        class="body-2"
        v-html="$t(`products.${productId}.description`)"
      />

      <div class="product__extra">
        <template
          v-if="
            $te(`products.${props.product.productId}.benefitPath`) &&
              $t(`products.${props.product.productId}.benefitPath`)
          "
        >
          <basic-text-button
            :label="$t('comparison.link')"
            @click="$emit('switch-view', 'benefits')"
          >
            <template #trailing>
              <the-icon
                art="light"
                name="list-check"
              />
            </template>
          </basic-text-button>
        </template>

        <template v-if="showHospitalSearch">
          <basic-text-button
            :label="$t('product.detail.hospitalSearch')"
            @click="$emit('switch-view', 'hospital')"
          >
            <template #trailing>
              <the-icon
                art="light"
                name="house-medical"
              />
            </template>
          </basic-text-button>
        </template>
      </div>
    </div>

    <div class="product__content">
      <ul class="product__bullets">
        <li
          v-for="(bullet, key) in $t(`products.${productId}.bulletPoints`)"
          :key="key"
          class="product__bullet"
        >
          <span v-text="bullet" />
          <the-icon
            v-if="$te(`products.${productId}.bulletPointsTooltip.${key}`)"
            v-tooltip="$t(`products.${productId}.bulletPointsTooltip.${key}`)"
            art="regular"
            name="circle-info"
            html-tooltip
          />
        </li>
      </ul>
    </div>

    <product-options
      v-if="$props.options"
      :category-id="$props.categoryId"
      :group-id="$props.groupId"
      :mode="$props.optionsMode"
      :person="$props.person"
      :product="$props.product"
      :show-doctor-reduction="false"
      :source="$props.source"
      @change="event => $emit('change', event)"
    />

    <!-- messages here -->
    <div class="product__message">
      <template
        v-for="(message, index) in messages"
        :key="`message-${index}`"
      >
        <basic-message
          :actions="message.actions"
          :closeable="message.closeable"
          :content="message.content"
          :severity="message.severity"
        />
      </template>
    </div>

    <div class="product__costs">
      <basic-product-price
        class="lead-1"
        :product="$props.product"
        :prefix="pricePrefix"
        :suffix="$t('product.currency')"
      />
    </div>
  </div>
</template>

<script setup>
  import { computed, ref } from 'vue'

  import useI18n from '@shared/hooks/useI18n'
  import useProductState from '@shared/hooks/useProductState'
  import usePersonSpecificProduct from '@shared/hooks/usePersonSpecificProduct'

  import { BasicMessage, BasicProductPrice, BasicProductPricing, BasicTextButton } from '@shared/components/Basic'
  import ProductOptions from '@shared/components/Product/Options'

  // HOOKS
  const { t } = useI18n()
  const { state } = useProductState()
  const { getProductMessages } = usePersonSpecificProduct()

  // INIT
  defineEmits(['change', 'close', 'switch-view'])
  const props = defineProps({
    categoryId: {
      type: String,
      required: true,
    },

    groupId: {
      type: String,
      required: true,
    },

    id: {
      type: String,
      required: true,
    },

    modalTitle: {
      type: String,
      default: null,
    },

    options: {
      type: Boolean,
      default: true,
    },

    optionsMode: {
      type: String,
      default: 'write',
    },

    person: {
      type: Object,
      required: true,
    },

    product: {
      type: Object,
      required: true,
    },

    readonly: {
      type: Boolean,
      default: false,
    },

    source: {
      type: String,
      required: true,
    },
  })

  // DATA
  const modalRef = ref()

  // COMPUTED
  const hasCalculationWarnings = computed(() => {
    return props.product.calculationWarnings?.length > 0
  })

  const messages = computed(() => {
    return getProductMessages({
      categoryId: props.categoryId,
      hasCalculationWarnings: hasCalculationWarnings.value,
      modalRef: modalRef.value,
      personId: personId.value,
      product: props.product,
      productState: productState.value,
    })
  })

  const personId = computed(() => {
    return props.person.personId
  })

  const pricePrefix = computed(() => {
    return props.product?.showDiscountGroup && !props.product.doctor ? t('product.asof') : ''
  })

  const productId = computed(() => {
    return props.product.productId
  })

  const productState = computed(() => {
    const partnerNumber = props.person.partnerNumber
    return state(partnerNumber, productId.value)
  })

  const showHospitalSearch = computed(() => {
    return productId.value === 'SAMB05Produkt'
  })

  // EPILOGUE
  defineExpose({ modalRef })
</script>

<style scoped>
.box {
  display: flex;
  flex-direction: column;
  gap: var(--fixed-spacing-fix-04);

  .product__pricing {
    margin: calc(-1 * var(--fixed-spacing-fix-02)) 0 0 0;
  }

  .product__extra {
    display: flex;
    flex-direction: column;
    gap: var(--fixed-spacing-fix-02);

    &:not(:empty) {
      margin: var(--fixed-spacing-fix-02) 0 var(--fixed-spacing-fix-04) 0;
    }
  }

  .product__content {
    margin: calc(-1 * var(--fixed-spacing-fix-06)) 0 0 0;
  }

  .product__bullets {
    padding: 0 0 0 var(--fixed-spacing-fix-06);
  }

  .product__bullet .icon {
    margin: 0 0 0 4px;
  }

  .product__message:not(:empty) {
    display: flex;
    flex-direction: column;
    gap: var(--fixed-spacing-fix-04);
    margin: var(--fixed-spacing-fix-02) 0;
  }

  & > div:empty {
    display: none;
  }
}

@media (--v-medium) {
  .box {
    .product__extra {
      flex-direction: row;
      gap: var(--fixed-spacing-fix-08);
    }
  }
}
</style>
